import { useEffect, useRef, useState } from 'react';
import {
  trackProductEvent,
  type ProductAnalyticsAdapter,
} from '@/lib/productAnalytics';

export const PWA_INSTALL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
export const PWA_INSTALL_DISMISSED_AT_KEY = 'prowycena_pwa_install_dismissed_at:v1';

export type PwaInstallOutcome = 'accepted' | 'dismissed' | 'error' | null;

export interface PwaInstallState {
  canInstall: boolean;
  isInstalled: boolean;
  isIos: boolean;
  isStandalone: boolean;
  installOutcome: PwaInstallOutcome;
  dismissedAt: number | null;
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

interface PwaInstallEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface PwaInstallStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PwaInstallEnvironment {
  eventTarget: PwaInstallEventTarget;
  storage: PwaInstallStorage | null;
  userAgent: string;
  isStandalone: () => boolean;
  now: () => number;
  track: ProductAnalyticsAdapter;
}

export interface PwaInstallManager {
  getSnapshot: () => PwaInstallState;
  subscribe: (listener: () => void) => () => void;
  start: () => void;
  stop: () => void;
  promptInstall: () => Promise<PwaInstallOutcome>;
}

export interface UsePwaInstallResult extends PwaInstallState {
  promptInstall: () => Promise<PwaInstallOutcome>;
}

const NOOP_EVENT_TARGET: PwaInstallEventTarget = {
  addEventListener: () => undefined,
  removeEventListener: () => undefined,
};

export function isIosUserAgent(userAgent: string): boolean {
  return /iphone|ipad|ipod/i.test(userAgent);
}

export function isDismissalCooldownActive(dismissedAt: number | null, now: number): boolean {
  return dismissedAt !== null && now - dismissedAt < PWA_INSTALL_COOLDOWN_MS;
}

function readDismissedAt(storage: PwaInstallStorage | null): number | null {
  if (!storage) return null;

  try {
    const value = Number(storage.getItem(PWA_INSTALL_DISMISSED_AT_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeDismissedAt(storage: PwaInstallStorage | null, dismissedAt: number): void {
  try {
    storage?.setItem(PWA_INSTALL_DISMISSED_AT_KEY, String(dismissedAt));
  } catch {
    // Storage can be unavailable in private browsing. The in-memory cooldown still applies.
  }
}

function clearDismissedAt(storage: PwaInstallStorage | null): void {
  try {
    storage?.removeItem(PWA_INSTALL_DISMISSED_AT_KEY);
  } catch {
    // Storage is optional; installation must continue without it.
  }
}

export function createPwaInstallManager(environment: PwaInstallEnvironment): PwaInstallManager {
  let deferredPrompt: BeforeInstallPromptEvent | null = null;
  let isStarted = false;
  const listeners = new Set<() => void>();
  const initialStandalone = environment.isStandalone();
  let state: PwaInstallState = {
    canInstall: false,
    isInstalled: initialStandalone,
    isIos: isIosUserAgent(environment.userAgent),
    isStandalone: initialStandalone,
    installOutcome: null,
    dismissedAt: readDismissedAt(environment.storage),
  };

  const publish = (patch: Partial<PwaInstallState>): void => {
    const next = { ...state, ...patch };
    state = {
      ...next,
      canInstall: deferredPrompt !== null &&
        !next.isInstalled &&
        !next.isStandalone &&
        !isDismissalCooldownActive(next.dismissedAt, environment.now()),
    };
    listeners.forEach((listener) => {
      listener();
    });
  };

  const handleBeforeInstallPrompt: EventListener = (rawEvent) => {
    const event = rawEvent as BeforeInstallPromptEvent;
    event.preventDefault();

    const isStandalone = environment.isStandalone();
    if (
      isStandalone ||
      state.isInstalled ||
      isDismissalCooldownActive(state.dismissedAt, environment.now())
    ) {
      deferredPrompt = null;
      publish({ isStandalone, isInstalled: state.isInstalled || isStandalone });
      return;
    }

    deferredPrompt = event;
    publish({ isStandalone: false });
  };

  const handleAppInstalled: EventListener = () => {
    deferredPrompt = null;
    clearDismissedAt(environment.storage);
    publish({
      isInstalled: true,
      isStandalone: environment.isStandalone(),
      installOutcome: 'accepted',
      dismissedAt: null,
    });
    environment.track('pwa_installed');
  };

  const start = (): void => {
    if (isStarted) return;
    isStarted = true;
    environment.eventTarget.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    environment.eventTarget.addEventListener('appinstalled', handleAppInstalled);
  };

  const stop = (): void => {
    if (!isStarted) return;
    isStarted = false;
    environment.eventTarget.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    environment.eventTarget.removeEventListener('appinstalled', handleAppInstalled);
  };

  const promptInstall = async (): Promise<PwaInstallOutcome> => {
    if (!deferredPrompt || !state.canInstall) return null;

    const promptEvent = deferredPrompt;
    deferredPrompt = null;
    publish({});
    environment.track('pwa_install_cta_clicked');

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;

      if (choice.outcome === 'accepted') {
        clearDismissedAt(environment.storage);
        publish({ installOutcome: 'accepted', dismissedAt: null });
        environment.track('pwa_install_prompt_accepted');
        return 'accepted';
      }

      const dismissedAt = environment.now();
      writeDismissedAt(environment.storage, dismissedAt);
      publish({ installOutcome: 'dismissed', dismissedAt });
      environment.track('pwa_install_prompt_dismissed');
      return 'dismissed';
    } catch {
      publish({ installOutcome: 'error' });
      return 'error';
    }
  };

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start,
    stop,
    promptInstall,
  };
}

function createBrowserEnvironment(): PwaInstallEnvironment {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      eventTarget: NOOP_EVENT_TARGET,
      storage: null,
      userAgent: '',
      isStandalone: () => false,
      now: Date.now,
      track: trackProductEvent,
    };
  }

  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };

  return {
    eventTarget: window,
    storage: window.localStorage,
    userAgent: navigator.userAgent,
    isStandalone: () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      navigatorWithStandalone.standalone === true,
    now: Date.now,
    track: trackProductEvent,
  };
}

export function usePwaInstall(): UsePwaInstallResult {
  const managerRef = useRef<PwaInstallManager | null>(null);
  if (!managerRef.current) {
    managerRef.current = createPwaInstallManager(createBrowserEnvironment());
  }

  const manager = managerRef.current;
  const [snapshot, setSnapshot] = useState<PwaInstallState>(() => manager.getSnapshot());

  useEffect(() => {
    const unsubscribe = manager.subscribe(() => {
      setSnapshot(manager.getSnapshot());
    });
    manager.start();
    setSnapshot(manager.getSnapshot());

    return () => {
      unsubscribe();
      manager.stop();
    };
  }, [manager]);

  return {
    ...snapshot,
    promptInstall: manager.promptInstall,
  };
}
